/**
 * WEBSOCKET / SIGNALR FAN SERVICE: TUG OF WAR (ESTIRA Y AFLOJA)
 * Wrapper de comunicación en tiempo real para el Aficionado Móvil de Estira y Afloja.
 * Gestiona la conexión persistente con SignalR Hub para recibir la cuenta regresiva sincronizada,
 * telemetría de tracción de soga a 100ms, cambios de fase, muerte súbita y pantalla de fin de partida.
 * 
 * @module utilities/WS/TugOfWarFanWS
 */

import SignalRService from './SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes genéricos.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el dispositivo móvil del aficionado al Hub de SignalR con el rol 'tugofwar:fan'.
 * Soporta polimórficamente tanto el orden de parámetros de Interaktia-V2 como el de componentes móviles heredados.
 * 
 * Eventos clave recibidos:
 * - 'tugofwar_countdown_tick': Tick de cuenta regresiva (3, 2, 1).
 * - 'tugofwar_match_started': Inicio de la partida, habilitación del botón de tap interactivo.
 * - 'tugofwar_telemetry': Telemetría de soga y fuerza de equipos a 100ms.
 * - 'tugofwar_sudden_death_started': Notificación de tiempo extra / muerte súbita.
 * - 'tugofwar_game_over': Fin de la partida con resultado (ganador, perdedor o empate).
 * - 'tugofwar_match_paused': Pausa de partida.
 * - 'tugofwar_match_resumed': Reanudación de partida.
 * - 'tugofwar_match_reset': Partida reiniciada.
 * - 'tugofwar_cover_shown': Retorno a portada.
 * 
 * @param {Function|string} [param1] - Callback onIncomingMessage o playerChoice ('player1' | 'player2' | 'J1' | 'J2').
 * @param {Function} [param2] - Callback onStatusChange o onIncomingMessage.
 * @param {string|Function} [param3] - Identificador GUID roomId o callback onStatusChange.
 * @param {string} [param4] - Identificador GUID roomId o playerChoice.
 */
export const ConnectWebSocketForTugOfWarFan = (
    param1,
    param2,
    param3,
    param4
) => {
    let onIncomingMessage = () => {};
    let onStatusChange = () => {};
    let roomId = '';
    let playerChoice = '';

    if (typeof param1 === 'function') {
        // Firma Interaktia V2: (onIncomingMessage, onStatusChange, roomId, playerChoice)
        onIncomingMessage = param1;
        onStatusChange = typeof param2 === 'function' ? param2 : () => {};
        roomId = typeof param3 === 'string' ? param3 : '';
        playerChoice = typeof param4 === 'string' ? param4 : '';
    } else {
        // Firma móvil directa: (playerChoice, onIncomingMessage, onStatusChange, roomId)
        playerChoice = typeof param1 === 'string' ? param1 : '';
        onIncomingMessage = typeof param2 === 'function' ? param2 : () => {};
        onStatusChange = typeof param3 === 'function' ? param3 : () => {};
        roomId = typeof param4 === 'string' ? param4 : '';
    }

    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala si fue provista
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(messageEnvelope);
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'tugofwar:fan',
        roomId: roomId || '',
        playerChoice: playerChoice || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[TugOfWarFanWS] Error al establecer conexión del aficionado con el Hub de SignalR:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del aficionado móvil y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForTugOfWarFan = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[TugOfWarFanWS] Error al desconectar la sesión de SignalR del aficionado:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForTugOfWarFan,
    DisconnectWebSocketForTugOfWarFan
};
